/**
 * IngredientModal Component
 * 
 * Displays detailed information about a selected ingredient.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Sparkles, 
  Heart, 
  Zap, 
  Shield, 
  Brain, 
  Droplets, 
  Leaf, 
  Star,
  Activity,
  Smile,
  Wind,
  RefreshCw,
  Battery,
  Waves,
  Target,
  Flame,
  Users,
  Wrench
} from 'lucide-react';
import type { Ingredient } from './ingredients.data';

interface IngredientModalProps {
  ingredient: Ingredient;
  onClose: () => void;
}

// Enhanced icon mapping for different benefit types
const getBenefitIcon = (title: string) => {
  const titleLower = title.toLowerCase();
  
  // Antioxidant support
  if (titleLower.includes('antioxidant')) {
    return <Shield className="w-5 h-5" />;
  }

  // General wellbeing
  if (titleLower.includes('wellbeing')) {
    return <Heart className="w-5 h-5" />;
  }
  
  // Energy and boost related
  if (titleLower.includes('energy') || titleLower.includes('boost')) {
    return <Zap className="w-5 h-5" />;
  }
  
  // Mental, brain, and clarity
  if (titleLower.includes('brain') || titleLower.includes('mental') || titleLower.includes('clarity')) {
    return <Brain className="w-5 h-5" />;
  }
  
  // Mood and emotional support
  if (titleLower.includes('mood') || titleLower.includes('stress') || titleLower.includes('calm')) {
    return <Smile className="w-5 h-5" />;
  }
  
  // Relaxation and calming
  if (titleLower.includes('relaxation') || titleLower.includes('relax') || titleLower.includes('nerve')) {
    return <Leaf className="w-5 h-5" />;
  }
  
  // Hydration and fluid balance
  if (titleLower.includes('hydration') || titleLower.includes('mineral') || titleLower.includes('electrolyte') || titleLower.includes('balance')) {
    return <Droplets className="w-5 h-5" />;
  }
  
  // Immune system and defense
  if (titleLower.includes('immune') || titleLower.includes('defense')) {
    return <Shield className="w-5 h-5" />;
  }
  
  // Muscle and physical
  if (titleLower.includes('muscle')) {
    return <Activity className="w-5 h-5" />;
  }
  
  // Default icon for any unmatched benefits
  return <Star className="w-5 h-5" />;
};

export const IngredientModal: React.FC<IngredientModalProps> = ({ 
  ingredient, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Show modal with animation on mount
  useEffect(() => {
    setIsAnimating(true);
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation to complete before calling onClose
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
    }, 300); // Match the transition duration
  };

  // Add keyboard event listener to close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 lg:p-6 transition-all duration-300 ease-out ${
        isVisible 
          ? 'opacity-100' 
          : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ingredient-modal-title"
    >
      {/* Enhanced backdrop with gradient and animation */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br from-black/90 via-purple-900/20 to-black/90 backdrop-blur-md transition-all duration-300 ease-out ${
          isVisible 
            ? 'opacity-100 backdrop-blur-md' 
            : 'opacity-0 backdrop-blur-none'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Modal container with enhanced styling and animations */}
      <div className={`relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden transition-all duration-300 ease-out ${
        isVisible 
          ? 'opacity-100 scale-100 translate-y-0' 
          : 'opacity-0 scale-95 translate-y-4'
      }`}>
        {/* Animated background glow */}
        <div className={`absolute inset-0 bg-gradient-to-r from-[#ff00ff]/10 via-transparent to-[#00ffff]/10 rounded-2xl blur-xl transition-all duration-500 ${
          isVisible ? 'animate-pulse' : 'opacity-0'
        }`} />
        
        {/* Main modal content */}
        <div className="relative bg-gradient-to-br from-gray-900/95 via-black/95 to-gray-900/95 backdrop-blur-xl rounded-2xl border border-[#ff00ff]/30 shadow-2xl shadow-[#ff00ff]/20 overflow-hidden">
          
          {/* Header with close button */}
          <div className={`relative p-4 sm:p-6 border-b border-[#ff00ff]/20 transition-all duration-300 delay-100 ${
            isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-4'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className={`w-6 h-6 text-[#ff00ff] transition-all duration-300 delay-200 ${
                  isVisible ? 'animate-pulse opacity-100' : 'opacity-0'
                }`} />
                <h2 
                  id="ingredient-modal-title"
                  className={`text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#ff00ff] to-[#00ffff] bg-clip-text text-transparent transition-all duration-300 delay-150 ${
                    isVisible 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 -translate-x-4'
                  }`}
                >
                  {ingredient.name}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className={`group p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#ff00ff]/50 transition-all duration-300 hover:scale-110 ${
                  isVisible 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 translate-x-4'
                }`}
                aria-label="Close details"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6 text-white/60 group-hover:text-white transition-colors" />
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]">
            <div className={`p-4 sm:p-6 lg:p-8 transition-all duration-300 delay-200 ${
              isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              
              {/* Mobile-first layout */}
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
                
                {/* Image section */}
                <div className={`flex-shrink-0 flex justify-center lg:w-1/3 transition-all duration-500 delay-300 ${
                  isVisible 
                    ? 'opacity-100 scale-100 translate-x-0' 
                    : 'opacity-0 scale-90 -translate-x-8'
                }`}>
                  <div className="relative group w-full h-full flex items-center">
                    {/* Glow effect behind image */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#ff00ff]/20 to-[#00ffff]/20 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
                    
                    {/* Image container */}
                    <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-4 sm:p-6 border border-white/10 backdrop-blur-sm w-full h-full flex items-center justify-center">
                      <img 
                        src={ingredient.image}
                        alt={ingredient.name}
                        className="w-full max-w-[200px] sm:max-w-[250px] lg:max-w-[300px] object-contain mx-auto transform group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Content section */}
                <div className={`flex-1 lg:w-2/3 transition-all duration-500 delay-400 ${
                  isVisible 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 translate-x-8'
                }`}>
                  
                  {/* Benefits section */}
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6 justify-center">
                      <Heart className={`w-6 h-6 text-[#ff00ff] transition-all duration-300 delay-500 ${
                        isVisible ? 'animate-pulse opacity-100' : 'opacity-0'
                      }`} />
                      <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent transition-all duration-300 delay-450 ${
                        isVisible 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-4'
                      }`}>
                        Health Benefits
                      </h3>
                    </div>
                    
                    {/* Benefits grid - Enhanced design */}
                    <div className="grid gap-4 sm:gap-5">
                      {ingredient.benefits.map((benefit, index) => (
                        <div 
                          key={index} 
                          className={`group relative overflow-hidden rounded-xl bg-gradient-to-r from-[#ff00ff]/10 via-transparent to-[#00ffff]/10 border border-[#ff00ff]/20 hover:border-[#ff00ff]/40 transition-all duration-300 hover:scale-[1.02] ${
                            isVisible 
                              ? 'opacity-100 translate-y-0' 
                              : 'opacity-0 translate-y-4'
                          }`}
                          style={{
                            transitionDelay: `${500 + index * 100}ms`
                          }}
                        >
                          {/* Animated background */}
                          <div className="absolute inset-0 bg-gradient-to-r from-[#ff00ff]/5 to-[#00ffff]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          {/* Content with enhanced layout */}
                          <div className="relative p-4 sm:p-5 flex items-start gap-4">
                            {/* Icon container */}
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-[#ff00ff]/20 to-[#00ffff]/20 border border-[#ff00ff]/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <div className="text-[#ff00ff] group-hover:text-white transition-colors duration-300">
                                {getBenefitIcon(benefit.title)}
                              </div>
                            </div>
                            
                            {/* Text content */}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-base sm:text-lg font-bold text-white mb-1 group-hover:text-[#ff00ff] transition-colors duration-300">
                                {benefit.title}
                              </h4>
                              <p className="text-sm sm:text-base text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer gradient */}
          <div className={`h-1 bg-gradient-to-r from-[#ff00ff] via-purple-500 to-[#00ffff] transition-all duration-300 delay-600 ${
            isVisible 
              ? 'opacity-100 scale-x-100' 
              : 'opacity-0 scale-x-0'
          }`} />
        </div>
      </div>
    </div>
  );

  // Use React Portal to render modal outside of component tree
  return createPortal(modalContent, document.body);
};